import {noop} from '@Utils/general.util';
import type {VoidFunction} from '@Utils/general.util';
import type {TeamsUpsellSource} from '@Libraries/teams-upsell.library';

export const PREMIUM_UPSELL_DIALOG_ENTRY_POINT_COOKIE = 'premiumUpsellDialogEntryPoint';

/**
 * Where in the product an upsell dialog was opened from, for dialogs that open from more than one place.
 * It is counted with each open and stored with the subscriber's attribution, next to the dialog name.
 * Values are lowercase snake_case ids of at most 64 characters (validated server-side).
 */
export enum UpsellDialogEntryPoint {
  ACCOUNTS_PAGE_TOP_BAR = 'accounts_page_top_bar',
  AI_CHAT_CONNECT_ACCOUNT = 'ai_chat_connect_account',
  AI_CHAT_PUBLISH = 'ai_chat_publish',
  AI_PROMPT_BOX_BRAND_KIT_SELECTOR = 'ai_prompt_box_brand_kit_selector',
  BRANDS_SELECT_MENU = 'brands_select_menu',
  BRAND_KIT_STYLE_TILE = 'brand_kit_style_tile',
  COLOR_PALETTE_PANEL = 'color_palette_panel',
  CROPPER_PANEL = 'cropper_panel',
  DOWNLOAD_PUBLISH_DIALOG = 'download_publish_dialog',
  EDITOR_ADD_BRAND_ASSET = 'editor_add_brand_asset',
  EDITOR_BRANDS_PANEL = 'editor_brands_panel',
  EDITOR_IMAGE_BG_REMOVAL_FAILURE = 'editor_image_bg_removal_failure',
  EDITOR_IMAGE_BOTTOM_BAR = 'editor_image_bottom_bar',
  EDITOR_MEDIA_SLIDE_BOTTOM_BAR = 'editor_media_slide_bottom_bar',
  EDITOR_NAV_BAR = 'editor_nav_bar',
  EDITOR_POPUP_MENU = 'editor_popup_menu',
  EDITOR_REMOVE_BG_MODE = 'editor_remove_bg_mode',
  EDITOR_SIDE_PANEL_BACKGROUND = 'editor_side_panel_background',
  EDITOR_SIDE_PANEL_IMAGE = 'editor_side_panel_image',
  EDITOR_SIDE_PANEL_SPELLCHECK = 'editor_side_panel_spellcheck',
  EDITOR_SIDE_PANEL_TEXT = 'editor_side_panel_text',
  EDITOR_SPELLCHECK_PANEL = 'editor_spellcheck_panel',
  EDITOR_TEXT_BOTTOM_BAR = 'editor_text_bottom_bar',
  EDITOR_TEXT_SLIDE_BOTTOM_BAR = 'editor_text_slide_bottom_bar',
  EDITOR_VIDEO_BG_REMOVAL_FAILURE = 'editor_video_bg_removal_failure',
  EDITOR_VIDEO_BG_REMOVAL_REQUEST = 'editor_video_bg_removal_request',
  EMAIL_CAMPAIGN_DIALOG = 'email_campaign_dialog',
  EMAIL_CAMPAIGN_MENU = 'email_campaign_menu',
  EMAIL_CAMPAIGN_PERFORMANCE_PAGE = 'email_campaign_performance_page',
  EMAIL_CAMPAIGN_PUBLISH_OPTIONS = 'email_campaign_publish_options',
  EMAIL_CAMPAIGN_SETTINGS = 'email_campaign_settings',
  EMAIL_MAKER = 'email_maker',
  EMAIL_MAKER_AI_REWRITE_DROPDOWN = 'email_maker_ai_rewrite_dropdown',
  EMAIL_MAKER_AUTO_UPDATE = 'email_maker_auto_update',
  EMAIL_MAKER_BLOCK_MANAGER = 'email_maker_block_manager',
  EVENT_ITEM_MENU = 'event_item_menu',
  EVENT_PAGE = 'event_page',
  EVENT_PROMOTION = 'event_promotion',
  FONT_SELECTOR_PANEL = 'font_selector_panel',
  FONT_UPLOAD_REJECTED = 'font_upload_rejected',
  MORE_ACCOUNTS_BANNER = 'more_accounts_banner',
  MYSTUFF_BRANDS_PAGE = 'mystuff_brands_page',
  MYSTUFF_MULTI_SELECT = 'mystuff_multi_select',
  MYSTUFF_SIDEBAR = 'mystuff_sidebar',
  MYSTUFF_TOP_NAV_TAB = 'mystuff_top_nav_tab',
  NEW_BRAND_BUTTON = 'new_brand_button',
  ONE_CLICK_PROMO_ACCOUNTS = 'one_click_promo_accounts',
  ONE_CLICK_PROMO_PUBLISH = 'one_click_promo_publish',
  POSTER_PUBLISHING_OPTIONS_PANEL = 'poster_publishing_options_panel',
  SOCIAL_ACCOUNT_FIX_PLAN_LOCKED = 'social_account_fix_plan_locked',
  SOCIAL_ACCOUNT_GRID_ITEM = 'social_account_grid_item',
  SOCIAL_PLAN_LOCKED_UPGRADE_BUTTON = 'social_plan_locked_upgrade_button',
  SOCIAL_PLATFORM_CONNECT_GRID = 'social_platform_connect_grid',
  SOCIAL_POSTS_DIALOG = 'social_posts_dialog',
  SOCIAL_POST_GRID_ITEM = 'social_post_grid_item',
  SOCIAL_POST_METRICS_OVERVIEW = 'social_post_metrics_overview',
  SOCIAL_POST_RESCHEDULE = 'social_post_reschedule',
  SOCIAL_PUBLISHING_PARAMS_PLAN_LOCKED = 'social_publishing_params_plan_locked',
  SOCIAL_PUBLISH_PLAN_LOCKED = 'social_publish_plan_locked',
  SOCIAL_WIZARD_ACCOUNTS_LIST = 'social_wizard_accounts_list',
  SOCIAL_WIZARD_ACCOUNT_LIST_ITEM = 'social_wizard_account_list_item',
  SOCIAL_WIZARD_DESIGN_ACTIONS = 'social_wizard_design_actions',
  SOCIAL_WIZARD_FOOTER = 'social_wizard_footer',
  SOCIAL_WIZARD_NAV = 'social_wizard_nav',
  SOCIAL_WIZARD_POST_DETAILS = 'social_wizard_post_details',
  STAFF_PREVIEW = 'staff_preview',
  TEMPLATE_PAGE = 'template_page',
}

/**
 * Any place a dialog open is credited to: a shared entry point, or the surface a Teams upsell opened from.
 */
export type AnyUpsellDialogEntryPoint = UpsellDialogEntryPoint | TeamsUpsellSource;

export interface PremiumOnlyFeatureDialogOptions {
  onDialogLoaded?: VoidFunction;
  shouldOpenInNewTab?: boolean;
  entryPoint?: AnyUpsellDialogEntryPoint;
}

/**
 * Counts one open of a tracked upsell dialog. Fire-and-forget: a failed count must never get in the way
 * of the dialog, so errors are dropped.
 */
export const recordUpsellDialogOpen = (upsellDialogName: string, entryPoint?: AnyUpsellDialogEntryPoint): void => {
  window.PMW.writeLocal('premium/recordUpsellDialogOpen', {dialog: upsellDialogName, entryPoint: entryPoint ?? ''}).catch(noop);
};
